// Pure merge logic for the Connectors page (fn-72.4 / R6).
//
// Connectors is a preview-only, ADDITIVE view. The model (decided with the
// product owner):
//   - A CONNECTOR is a DECLARED MCP server (`mcp-servers` — the config-repo
//     scan, e.g. `smokeball`, `infotrack`). That is the left-hand card.
//   - A connector shows ALL of its tools, SPLIT INTO GROUPS BY PREFIX, where the
//     prefix is parsed dynamically from each gateway tool id (`<prefix>___<tool>`).
//     We never key off a hand-maintained list of gateway target names.
//   - The live tool catalog (`gateway-tools`) is an introspection of ONE
//     endpoint; the response says which (`gateway_url`, set by the API layer).
//     The declared server whose URL MATCHES that endpoint (`sameMcpUrl`) owns
//     every tool it federates (all its prefixes) — so `smokeball`, pointed at
//     the gateway, shows the smokeball / forms / mattertypes / archie groups
//     together rather than fragmenting into separate cards. A declared server
//     pointed elsewhere (`infotrack`) is its own connector and is not fed those
//     tools. This is purely URL-based — no provider-specific (AWS/gateway)
//     logic in the frontend.
//
// This module is pure + unit-tested; the page wrapper owns the two queries and
// the gateway error/forbidden surfacing.

import type { GatewayTargetResult } from '../gateway-tools/list.js';
import type { McpServerRow } from '../mcp-servers/list.js';

/** A single tool on a connector. */
export interface ConnectorTool {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly readWrite: GatewayTargetToolReadWrite;
  readonly inputSchema: unknown;
}

// Derive the read/write hint type from the gateway target tool shape so a
// contract change is a compile error here, not a silent drift.
type GatewayOkTarget = Extract<GatewayTargetResult, { ok: true }>;
type GatewayTargetToolReadWrite = GatewayOkTarget['tools'][number]['read_write'];

/** One prefix group within a connector — the tools that share an id prefix. */
export interface ConnectorToolGroup {
  /** Raw prefix parsed from the tool ids (e.g. `forms`). */
  readonly prefix: string;
  /** Human label for the group (prettified prefix). */
  readonly label: string;
  readonly tools: readonly ConnectorTool[];
}

/** A gateway target that failed to load — surfaced, never silently dropped. */
export interface GatewayTargetError {
  readonly name: string;
  readonly code: string;
}

/**
 * Whether a connector's tools come from the introspected gateway:
 *   - `gateway`  — its URL matches the introspected endpoint; it owns those tools.
 *   - `external` — its URL differs from the (known) endpoint; served elsewhere.
 *   - `unknown`  — we have no endpoint to compare against yet (the live query is
 *     pending/failed/forbidden, so `gateway_url` is absent). NOT "external" —
 *     the page resolves it from the query flags rather than mislabeling it.
 */
export type ConnectorOwnership = 'gateway' | 'external' | 'unknown';

/**
 * A connector = one declared MCP server. `name` is the stable, normalized
 * (lowercased) key used in the URL `?connector=`; `displayName` is the human
 * label. `ownership` is `gateway` only when the server's URL matches the
 * introspected gateway endpoint — only then is it fed the live gateway tools
 * (in `toolGroups`, split by prefix).
 */
export interface Connector {
  readonly name: string;
  readonly displayName: string;
  readonly declared: McpServerRow | null;
  readonly ownership: ConnectorOwnership;
  readonly toolGroups: readonly ConnectorToolGroup[];
  /** Gateway targets that failed to load (partial result) — rendered as warnings. */
  readonly targetErrors: readonly GatewayTargetError[];
  /** Total live tools across all groups. */
  readonly toolCount: number;
}

/** Normalized grouping key — token-preserving (case + separator canonical). */
function normalizeKey(name: string): string {
  return name
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Human label from a raw name/prefix: split on `_`/`-`/camelCase, title-case.
 * `matter-context` → "Matter Context", `smokeball` → "Smokeball".
 */
export function prettifyConnectorName(name: string): string {
  const spaced = name
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  if (spaced.length === 0) return name;
  return spaced.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The prefix of a gateway tool id (`<prefix>___<tool>`) — the segment before
 * the FIRST `___`. Returns '' when there is no separator (the tool is then
 * grouped under an "Other" bucket by the caller).
 */
export function toolPrefix(toolId: string): string {
  const i = toolId.indexOf('___');
  return i < 0 ? '' : toolId.slice(0, i);
}

/**
 * Whether two MCP endpoint URLs refer to the same server. Compared on a
 * normalized form (lowercased host, trailing slashes trimmed, query/hash
 * ignored) so a declared server's URL matches the gateway URL the tools were
 * introspected from. Purely URL-based — NO provider-specific logic.
 */
export function sameMcpUrl(a: string | null, b: string | null): boolean {
  if (a === null || b === null || a.length === 0 || b.length === 0) return false;
  const norm = (u: string): string => {
    try {
      const url = new URL(u);
      return `${url.protocol}//${url.host.toLowerCase()}${url.pathname.replace(/\/+$/, '')}`;
    } catch {
      return u.trim().replace(/\/+$/, '');
    }
  };
  return norm(a) === norm(b);
}

/**
 * Collect the live tools across OK targets AND the errors from failed ones —
 * a failed target is surfaced (`errors`), never silently dropped.
 */
function collectGatewayTools(targets: readonly GatewayTargetResult[]): {
  readonly tools: ConnectorTool[];
  readonly errors: GatewayTargetError[];
} {
  const tools: ConnectorTool[] = [];
  const errors: GatewayTargetError[] = [];
  for (const entry of targets) {
    if (!entry.ok) {
      errors.push({ name: entry.target.name, code: entry.error.code });
      continue;
    }
    for (const tool of entry.tools) {
      tools.push({
        id: tool.id,
        name: tool.name,
        description: tool.description,
        readWrite: tool.read_write,
        inputSchema: tool.input_schema,
      });
    }
  }
  return { tools, errors };
}

/** Group tools by their id prefix, sorted by label; '' prefix → "Other". */
function groupToolsByPrefix(tools: readonly ConnectorTool[]): ConnectorToolGroup[] {
  const byPrefix = new Map<string, ConnectorTool[]>();
  for (const tool of tools) {
    const prefix = toolPrefix(tool.id);
    const list = byPrefix.get(prefix);
    if (list === undefined) byPrefix.set(prefix, [tool]);
    else list.push(tool);
  }
  const groups: ConnectorToolGroup[] = [];
  for (const [prefix, groupTools] of byPrefix) {
    groups.push({
      prefix,
      label: prefix.length === 0 ? 'Other' : prettifyConnectorName(prefix),
      tools: groupTools,
    });
  }
  groups.sort((a, b) => a.label.localeCompare(b.label));
  return groups;
}

/**
 * Build the connector list. Connectors are the DECLARED servers; the
 * gateway-backed one is fed the live gateway tools split into prefix groups.
 *
 * `targets === null` means the gateway-tools query is unavailable (pending,
 * error, or forbidden) — gateway-backed connectors then have empty `toolGroups`
 * and the page surfaces WHY (banner + detail notice).
 *
 * If the gateway returns tools but NO declared server is gateway-backed (e.g.
 * `mcp-servers` failed to load), a synthetic "Gateway tools" connector is added
 * so nothing is silently dropped.
 */
export function buildConnectors(args: {
  readonly servers: readonly McpServerRow[];
  readonly targets: readonly GatewayTargetResult[] | null;
  /**
   * The gateway endpoint the live tools were introspected from (the response's
   * `gateway_url`). A declared server owns those tools when its URL matches —
   * purely URL-based, no provider-specific detection.
   */
  readonly gatewayUrl: string | null;
}): Connector[] {
  const live = args.targets === null ? null : collectGatewayTools(args.targets);
  const groups = live === null ? null : groupToolsByPrefix(live.tools);
  const targetErrors = live === null ? [] : live.errors;

  // Collision-safe keys: distinct server names that normalize to the same key
  // (e.g. `matter-context` + `matterContext`) get a `-2`/`-3` suffix so React
  // keys stay unique and `findConnector` can reach each one.
  const usedKeys = new Set<string>();
  const uniqueKey = (base: string): string => {
    const root = base.length > 0 ? base : 'connector';
    let key = root;
    let n = 2;
    while (usedKeys.has(key)) key = `${root}-${n++}`;
    usedKeys.add(key);
    return key;
  };

  const connectors: Connector[] = args.servers.map((server) => {
    // No endpoint to compare against (failed/pending live query) ⇒ unknown,
    // never "external" — the page decides from the query flags.
    const ownership: ConnectorOwnership =
      args.gatewayUrl === null
        ? 'unknown'
        : sameMcpUrl(server.url, args.gatewayUrl)
          ? 'gateway'
          : 'external';
    const isGateway = ownership === 'gateway';
    const toolGroups = isGateway && groups !== null ? groups : [];
    return {
      name: uniqueKey(normalizeKey(server.name)),
      displayName: prettifyConnectorName(server.name),
      declared: server,
      ownership,
      toolGroups,
      targetErrors: isGateway ? targetErrors : [],
      toolCount: toolGroups.reduce((n, g) => n + g.tools.length, 0),
    };
  });

  // Fallback: live tools/errors with no declared owner — surface, don't drop.
  const liveHasContent = groups !== null && (groups.length > 0 || targetErrors.length > 0);
  if (liveHasContent && !connectors.some((c) => c.ownership === 'gateway')) {
    connectors.push({
      name: uniqueKey('gateway-tools'),
      displayName: 'Gateway tools',
      declared: null,
      ownership: 'gateway',
      toolGroups: groups ?? [],
      targetErrors,
      toolCount: (groups ?? []).reduce((n, g) => n + g.tools.length, 0),
    });
  }

  connectors.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return connectors;
}

/**
 * Resolve the selected connector from the URL `?connector=` param. Matches on
 * the normalized key, so an old/mixed-case URL still resolves.
 */
export function findConnector(
  connectors: readonly Connector[],
  name: string | null,
): Connector | undefined {
  if (name === null) return undefined;
  const key = normalizeKey(name);
  return connectors.find((connector) => connector.name === key);
}
